// TEMPLATE: Copy and modify this file for other service pages (e.g., DeckCleaning, PatioCleaning, etc.)
import React from 'react';
import CTA from '../components/CTA';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const FenceCleaning = () => {
  const serviceSEO = {
    title: 'Fence Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional fence cleaning in Phoenix, AZ. Remove stains & algae. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Fence Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Fence Cleaning'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Fence Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional fence cleaning in Phoenix, AZ. Remove stains & algae. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="wood fence cleaning, vinyl fence cleaning, metal fence cleaning, Phoenix fence maintenance, fence stain removal, fence algae treatment, privacy fence cleaning, fence restoration, fence surface cleaning, fence longevity"
      />
      {/* Hero/Banner Image */}
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Fence Cleaning Banner">
        <picture>
          <source srcSet="/fence_hero.avif" type="image/avif" />
          <source srcSet="/fence_hero.webp" type="image/webp" />
        <img 
          src="/fence_hero.png" 
          alt="Professional fence cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Fence Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Fence Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          At Copper Canyon Pressure Wash, we offer professional fence cleaning services for homeowners in the greater Phoenix area, based in Scottsdale. Fences take a beating from Arizona's harsh sun, dust storms, and occasional monsoon rains, leading to dirt buildup, algae growth, and faded finishes. Our team uses specialized pressure washing techniques to clean and restore your fence, enhancing your property's curb appeal and longevity.
        </p>
        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Longer-Lasting Fences</h3>
            </div>
            <p className="text-gray-700">We remove dirt and algae that can degrade your fence over time, helping it last longer in Arizona's tough climate.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Curb Appeal Refresh</h3>
            </div>
            <p className="text-gray-700">Our cleaning methods refresh the look of your fence, whether it's wood, vinyl, or metal, making your property stand out.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Eco-Friendly Cleaning</h3>
            </div>
            <p className="text-gray-700">We use eco-friendly cleaners to tackle grime without harming your fence's finish or nearby landscaping.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Boosted Home Appearance</h3>
            </div>
            <p className="text-gray-700">A clean fence boosts your home's appearance, creating a polished look for your outdoor space.</p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Fence Cleaning Process</h2>
        <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Inspection</h3>
                <p className="text-gray-700">We start by inspecting your fence for dirt, algae, or damage, identifying areas that need special attention.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Biodegradable Pre-Treatment</h3>
                <p className="text-gray-700">We apply biodegradable detergents to break down grime and organic growth like algae or mildew.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Gentle or Moderate Cleaning</h3>
                <p className="text-gray-700">Using adjustable pressure washers, we clean with a low setting for wood to prevent splintering, or a moderate setting for vinyl and metal.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Rinse & Inspection</h3>
                <p className="text-gray-700">We finish with a thorough rinse and inspection, ensuring your fence is clean and its finish is well-preserved.</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Fence Cleaning</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Precise Pressure Settings</h3>
            <p className="text-gray-700">We use precise pressure settings—like 800 PSI for wood or 1,500 PSI for vinyl—to avoid damage like splintering or peeling paint.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">All Fence Materials</h3>
            <p className="text-gray-700">We're experienced with all fence materials, including cedar, vinyl, aluminum, and wrought iron, ensuring each gets the right treatment.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
            <p className="text-gray-700">Our expertise prevents issues like warped boards or faded coatings, which can happen with inexperienced cleaners using improper methods.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Arizona Fence Know-How</h3>
            <p className="text-gray-700">We understand how Arizona's climate impacts fences, delivering a clean that protects against sun exposure and moisture damage.</p>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Fence Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our fence cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ
        items={[
          {
            question: 'How often should I have my fence cleaned?',
            answer: 'We recommend cleaning your fence annually to keep it looking its best and to prevent buildup of dirt and algae.'
          },
          {
            question: 'Is your fence cleaning process safe for all materials?',
            answer: 'Yes, we tailor our cleaning methods to the specific material of your fence, whether it\'s wood, vinyl, or metal.'
          },
          {
            question: 'Can you remove tough stains or graffiti from my fence?',
            answer: 'Yes, our team is equipped to handle tough stains and graffiti removal for most fence types.'
          }
        ]}
        title="Frequently Asked Questions About Fence Cleaning"
      />
    </div>
  );
};

export default FenceCleaning; 